import { useEffect, useMemo, useState } from 'react';
import { CrudForm } from '../shared/CrudPage';
import { stockTransferService, stockService } from '@kss/api';
import type {
  StockTransfer,
  StockTransferCreate,
  StockTransferFilter,
  StockTransferItem,
  Stock,
} from '@kss/api';
import { loadWarehouseOptions, loadProductOptions } from '../shared/formLoaders';
import { SearchableSelect, Button, type SearchableSelectOption } from '@kss/ui';
import { Plus, Trash2 } from 'lucide-react';
import NumberField from '../../components/NumberField';

/* SSO ISSUE 6 (2026-07-25) — Stock Transfer used to be a header-only form
   with just From/To warehouses; the client called it "transfer everything"
   and the backend never moved actual Stock either. This rewrite adds an
   editable line-item grid (SearchableSelect product picker + qty input +
   live available-quantity hint from the FROM warehouse). Submit is blocked
   when the grid is empty or any line's qty exceeds what's on hand. Backend
   half of the fix cascades the items and applies the paired Out/In stock
   movements — see StockTransferService.AddAsync. */

type LineRow = {
  key: string;
  productId: string;
  quantity: number;
};

type F = {
  fromWarehouseId: string;
  toWarehouseId: string;
  transferNumber: string;
  transferDate: string;
  status: string;
  notes: string;
  items: LineRow[];
};

const STATUSES = ['Pending', 'InTransit', 'Received', 'Cancelled'];
const today = new Date().toISOString().slice(0, 10);

const newLineRow = (): LineRow => ({
  key: `l_${Math.random().toString(36).slice(2, 10)}`,
  productId: '',
  quantity: 1,
});

const empty: F = {
  fromWarehouseId: '',
  toWarehouseId: '',
  transferNumber: '',
  transferDate: today,
  status: 'Pending',
  notes: '',
  items: [newLineRow()],
};

/**
 * Editable line-item grid for a StockTransfer. Every row picks a Product
 * and a Quantity; a live "on-hand at From warehouse" hint prevents the
 * merchant from queuing an impossible move. The picker + qty pairs get
 * projected into StockTransferItem[] on save.
 *
 * Available-qty lookup: we fetch every Stock row for the From warehouse
 * once (via /Stock/filter { warehouseId }) and index by productId, so
 * changing the product on a row updates the hint without another network
 * hop. Re-fetches when the merchant switches the From warehouse.
 */
function ItemsGrid({
  value,
  onChange,
  fromWarehouseId,
}: {
  value: LineRow[];
  onChange: (next: LineRow[]) => void;
  fromWarehouseId: string;
}) {
  const [productOptions, setProductOptions] = useState<SearchableSelectOption[]>([]);
  useEffect(() => {
    let cancelled = false;
    loadProductOptions()
      .then((opts) => {
        if (!cancelled) setProductOptions(opts);
      })
      .catch(() => {
        /* swallow — inline error surface handles messaging */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Available-quantity index for the currently selected From warehouse.
  // Keyed by productId so the qty column can annotate itself instantly
  // as the user switches products between rows.
  const [availableByProduct, setAvailableByProduct] = useState<Record<string, number>>({});
  useEffect(() => {
    setAvailableByProduct({});
    if (!fromWarehouseId) return;
    let cancelled = false;
    stockService
      .filter({ warehouseId: fromWarehouseId })
      .then((rows: Stock[]) => {
        if (cancelled) return;
        const idx: Record<string, number> = {};
        for (const r of rows ?? []) {
          const pid = r.productId;
          if (!pid) continue;
          const onHand = Number(r.quantity ?? 0) - Number(r.reservedQuantity ?? 0);
          // If a product has rows across multiple variants at the same
          // warehouse the panel picker collapses them — sum on-hand per
          // product so the hint reflects the total the merchant can move.
          idx[pid] = (idx[pid] ?? 0) + (Number.isFinite(onHand) ? onHand : 0);
        }
        setAvailableByProduct(idx);
      })
      .catch(() => {
        // Warehouse might genuinely have no stock or the filter endpoint
        // is temporarily down; either way an empty index just makes the
        // hint read "0 available" — which is honest.
        if (!cancelled) setAvailableByProduct({});
      });
    return () => {
      cancelled = true;
    };
  }, [fromWarehouseId]);

  const patchRow = (idx: number, patch: Partial<LineRow>) => {
    const next = value.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const addRow = () => onChange([...value, newLineRow()]);
  const removeRow = (idx: number) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next.length === 0 ? [newLineRow()] : next);
  };

  const grandTotal = useMemo(
    () =>
      value.reduce((acc, l) => acc + (Number.isFinite(l.quantity) ? Number(l.quantity) : 0), 0),
    [value],
  );

  return (
    <div className="md:col-span-2">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-content-secondary">
          Line items <span className="text-danger">*</span>
        </span>
        <Button type="button" variant="outline" size="sm" onClick={addRow}>
          <Plus className="h-3.5 w-3.5" /> Add row
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-card-hover text-xs uppercase text-content-muted">
            <tr>
              <th className="w-[54%] px-2 py-2 text-left">Product</th>
              <th className="w-[20%] px-2 py-2 text-right">Qty to transfer</th>
              <th className="w-[20%] px-2 py-2 text-right">Available (From)</th>
              <th className="w-[6%] px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {value.map((row, idx) => {
              const avail = row.productId ? availableByProduct[row.productId] ?? 0 : 0;
              const overCommit = row.productId && Number(row.quantity) > avail;
              return (
                <tr key={row.key} className="border-t border-border align-top">
                  <td className="px-2 py-1.5">
                    <SearchableSelect
                      value={row.productId}
                      onChange={(v) => patchRow(idx, { productId: v })}
                      options={productOptions}
                      placeholder="Pick a product"
                      searchPlaceholder="Search products…"
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <NumberField
                      min={0}
                      step="1"
                      value={row.quantity}
                      onValueChange={(n) => patchRow(idx, { quantity: n })}
                      className={
                        'h-9 w-full rounded-md border bg-surface-card px-2 text-right text-sm outline-none focus:border-primary ' +
                        (overCommit ? 'border-danger' : 'border-border')
                      }
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right text-sm text-content-primary">
                    {row.productId ? (
                      <span className={overCommit ? 'text-danger font-medium' : 'text-content-secondary'}>
                        {avail}
                      </span>
                    ) : (
                      <span className="text-content-muted">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
                      onClick={() => removeRow(idx)}
                      title="Remove row"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-border bg-surface-card-hover">
              <td className="px-2 py-2 text-right text-xs font-medium text-content-secondary">
                Grand total (units)
              </td>
              <td className="px-2 py-2 text-right text-sm font-semibold text-content-primary">
                {grandTotal}
              </td>
              <td></td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="mt-1 text-[11px] text-content-muted">
        {fromWarehouseId
          ? 'Available quantity is on-hand minus reserved at the From warehouse. Backend blocks any line whose qty exceeds on hand.'
          : 'Pick a From warehouse first — available quantities appear once we know where the stock is moving out of.'}
      </p>
    </div>
  );
}

export default function NewStockTransfer() {
  return (
    <CrudForm<StockTransfer, StockTransferCreate, StockTransferFilter, F>
      title="stock transfer"
      service={stockTransferService}
      idKey="stockTransferId"
      basePath="/panel/stock-transfers"
      emptyForm={empty}
      fields={[
        { key: 'transferNumber', label: 'Transfer #', required: true, placeholder: 'XFR-2026-0001' },
        { key: 'transferDate', label: 'Date', type: 'date', required: true },
        { key: 'fromWarehouseId', label: 'From warehouse', type: 'search', loadOptions: loadWarehouseOptions, required: true },
        { key: 'toWarehouseId', label: 'To warehouse', type: 'search', loadOptions: loadWarehouseOptions, required: true },
        { key: 'status', label: 'Status', type: 'select', options: STATUSES.map((v) => ({ value: v, label: v })) },
        {
          key: 'items',
          label: 'Line items',
          type: 'custom',
          fullWidth: true,
          render: (val, set, form) => (
            <ItemsGrid
              value={val as LineRow[]}
              onChange={(next) => set(next as F[keyof F & string])}
              fromWarehouseId={form.fromWarehouseId}
            />
          ),
        },
        { key: 'notes', label: 'Notes', type: 'textarea', fullWidth: true },
      ]}
      fromEntity={(r) => ({
        fromWarehouseId: r.fromWarehouseId ?? '',
        toWarehouseId: r.toWarehouseId ?? '',
        transferNumber: r.transferNumber ?? '',
        transferDate: r.transferDate?.slice(0, 10) ?? today,
        status: r.status ?? 'Pending',
        notes: r.notes ?? '',
        items:
          (r.items ?? []).length > 0
            ? (r.items ?? []).map(
                (i, idx): LineRow => ({
                  key: `edit_${i.stockTransferItemId ?? idx}`,
                  productId: i.productId ?? '',
                  quantity: Number(i.quantity ?? 0),
                }),
              )
            : [newLineRow()],
      })}
      toEntity={(f, id) => {
        /* Line items MUST omit stockTransferId on Create — same reason
           PurchaseOrder omits purchaseOrderId (backend binder rejects the
           empty-string coercion to Guid). The service's MapHeaderWithItems
           stamps the id onto every line before persisting. */
        const wireItems: StockTransferItem[] = f.items
          .filter((l) => l.productId && Number(l.quantity) > 0)
          .map((l) => ({
            // stockTransferId is Guid on the backend and non-nullable in
            // the TS interface; omitting the field would break the compile,
            // so we stamp the header id if we have it (edit flow) and a
            // zero-guid placeholder for create (backend re-stamps).
            stockTransferId: id ?? '00000000-0000-0000-0000-000000000000',
            productId: l.productId,
            quantity: Number(l.quantity),
          }));
        return {
          stockTransferId: id,
          fromWarehouseId: f.fromWarehouseId,
          toWarehouseId: f.toWarehouseId,
          transferNumber: f.transferNumber || undefined,
          transferDate: f.transferDate || undefined,
          status: f.status || undefined,
          notes: f.notes || undefined,
          items: wireItems,
        };
      }}
      validate={(f) => {
        if (!f.fromWarehouseId.trim()) return 'From warehouse is required';
        if (!f.toWarehouseId.trim()) return 'To warehouse is required';
        if (f.fromWarehouseId === f.toWarehouseId) return 'From and To warehouses must differ';
        const goodLines = f.items.filter((l) => l.productId && Number(l.quantity) > 0);
        if (goodLines.length === 0)
          return 'Add at least one line item with a product and quantity > 0.';
        return null;
      }}
    />
  );
}
